import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Plus,
  Trash2,
  AlertCircle,
  Check,
  X,
  Database,
  Calendar,
  Type,
  Hash,
  ToggleLeft,
  FileText,
  RefreshCw,
  Edit2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CustomFieldManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { entityName } = useParams();
  const [customFields, setCustomFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deletingField, setDeletingField] = useState(false);
  const [renameModal, setRenameModal] = useState(null);
  const [renamingField, setRenamingField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');

  const isThai = i18n?.language?.toLowerCase().startsWith('th') || false;

  const getLocalizedDisplayName = (field, fallback) => {
    if (!field) return fallback;
    if (isThai) {
      return field.displayNameTH || field.displayName || fallback;
    }
    return field.displayName || field.displayNameTH || fallback;
  };

  useEffect(() => {
    if (entityName) {
      loadCustomFields();
    }
  }, [entityName]);

  const loadCustomFields = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/custom-fields/${entityName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Tenant-Code': localStorage.getItem('tenantCode') || 'DEMO'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load custom fields');
      }

      const data = await response.json();
      setCustomFields(data.customFields || {});
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (fieldName) => {
    // Get usage statistics
    try {
      const response = await fetch(`/api/custom-fields/${entityName}/${fieldName}/usage`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Tenant-Code': localStorage.getItem('tenantCode') || 'DEMO'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDeleteModal({
          fieldName,
          usageCount: data.usageCount || 0
        });
      } else {
        setDeleteModal({
          fieldName,
          usageCount: 0
        });
      }
    } catch (err) {
      console.error('Error getting usage stats:', err);
      setDeleteModal({
        fieldName,
        usageCount: 0
      });
    }
  };

  const handleDeleteConfirm = async (cleanupData) => {
    if (!deleteModal) return;

    setDeletingField(true);
    try {
      const response = await fetch(
        `/api/custom-fields/${entityName}/${deleteModal.fieldName}?cleanupData=${cleanupData}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Tenant-Code': localStorage.getItem('tenantCode') || 'DEMO'
          }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete custom field');
      }

      const data = await response.json();
      
      if (cleanupData && data.affectedRecords > 0) {
        toast.success(`Custom field deleted and ${data.affectedRecords} records cleaned up`);
      } else {
        toast.success('Custom field deleted successfully');
      }

      setDeleteModal(null);
      await loadCustomFields();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingField(false);
    }
  };

  const handleRenameClick = (fieldName) => {
    setRenameModal({ fieldName });
    setNewFieldName(fieldName);
  };

  const handleRenameConfirm = async () => {
    if (!renameModal || !newFieldName.trim()) {
      toast.error('Please enter a new field name');
      return;
    }

    if (newFieldName.trim() === renameModal.fieldName) {
      toast.error('New field name must be different');
      return;
    }

    setRenamingField(true);
    try {
      const response = await fetch(
        `/api/custom-fields/${entityName}/${renameModal.fieldName}/rename`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Tenant-Code': localStorage.getItem('tenantCode') || 'DEMO'
          },
          body: JSON.stringify({
            newFieldName: newFieldName.trim()
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to rename custom field');
      }

      const data = await response.json();
      
      if (data.affectedRecords > 0) {
        toast.success(`Field renamed and ${data.affectedRecords} records updated`);
      } else {
        toast.success('Field renamed successfully');
      }

      setRenameModal(null);
      setNewFieldName('');
      await loadCustomFields();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRenamingField(false);
    }
  };

  const getFieldTypeIcon = (field) => {
    const type = field.metadata?.attributeType || field.ui?.inputType || field.dataType;
    switch (type) {
      case 'date':
      case 'datetime':
        return <Calendar className="h-5 w-5" />;
      case 'number':
      case 'integer':
      case 'decimal':
        return <Hash className="h-5 w-5" />;
      case 'checkbox':
      case 'boolean':
        return <ToggleLeft className="h-5 w-5" />;
      case 'textarea':
        return <FileText className="h-5 w-5" />;
      default:
        return <Type className="h-5 w-5" />;
    }
  };

  const getFieldTypeLabel = (field) => {
    const type = field.metadata?.attributeType || field.ui?.inputType || field.dataType;
    if (!type) return '';
    const key = `customFields.typeLabels.${type}`;
    const translated = t(key, {
      defaultValue: type.charAt(0).toUpperCase() + type.slice(1)
    });
    return translated;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const fieldCount = Object.keys(customFields).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/entity-config')}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <Database className="h-7 w-7" />
              <span>
                {t('customFields.title', 'Custom Fields')} -{' '}
                {entityName.charAt(0).toUpperCase() + entityName.slice(1)}
              </span>
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              {t('customFields.subtitle', 'Manage custom attribute fields for this entity')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadCustomFields}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => navigate(`/customers/new`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Field</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Custom Fields List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('customFields.heading', 'Custom Fields')} ({fieldCount})
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t(
              'customFields.storageNote',
              'These fields are stored in the Attributes column as JSON'
            )}
          </p>
        </div>

        {fieldCount === 0 ? (
          <div className="p-12 text-center">
            <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300">
              {t('customFields.emptyState.title', 'No custom fields defined yet')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('customFields.emptyState.help', {
                defaultValue: 'Add custom fields from the {{entity}} form',
                entity: entityName
              })}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {Object.entries(customFields).map(([fieldName, field]) => (
              <div
                key={fieldName}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                      {getFieldTypeIcon(field)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {getLocalizedDisplayName(field, fieldName)}
                        </h3>
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                          {getFieldTypeLabel(field)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Field name: <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{fieldName}</code>
                      </p>
                      {field.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          {field.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                        {field.metadata?.createdAt && (
                          <span>
                            Created: {new Date(field.metadata.createdAt).toLocaleDateString()}
                          </span>
                        )}
                        {field.validation?.required && (
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            Required
                          </span>
                        )}
                        {field.ui?.showInList && (
                          <span>Shown in list</span>
                        )}
                        {field.ui?.showInForm && (
                          <span>Shown in form</span>
                        )}
                        {field.ui?.showInDetail && (
                          <span>Shown in detail</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleRenameClick(fieldName)}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Rename field"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(fieldName)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete field"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rename Modal */}
      {renameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
                  <Edit2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Rename Custom Field
                </h3>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t('customFields.rename.messagePrefix', 'Rename the field')}{' '}
                <strong>
                  {getLocalizedDisplayName(
                    customFields[renameModal.fieldName],
                    renameModal.fieldName
                  )}
                </strong>
              </p>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> This will update the field name in the configuration and all existing data in the database.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Field Name
                </label>
                <input
                  type="text"
                  value={renameModal.fieldName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Field Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !renamingField) {
                      handleRenameConfirm();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                  placeholder="e.g., attr_newFieldName"
                  autoFocus
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use camelCase or snake_case format
                </p>
              </div>

              <div className="flex flex-col space-y-2">
                <button
                  onClick={handleRenameConfirm}
                  disabled={renamingField || !newFieldName.trim()}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {renamingField ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Renaming...</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Rename Field</span>
                    </>
                  )}
                </button>

                <button
                  onClick={() => {
                    setRenameModal(null);
                    setNewFieldName('');
                  }}
                  disabled={renamingField}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Custom Field
                </h3>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Are you sure you want to delete the field "<strong>{customFields[deleteModal.fieldName]?.displayName}</strong>"?
              </p>

              {deleteModal.usageCount > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Warning:</strong> This field is currently used in approximately <strong>{deleteModal.usageCount}</strong> record(s).
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                    You can choose to:
                  </p>
                  <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-1 ml-4 list-disc">
                    <li>Remove the field definition only (data will remain in database)</li>
                    <li>Remove the field definition AND clean up all data</li>
                  </ul>
                </div>
              )}

              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => handleDeleteConfirm(false)}
                  disabled={deletingField}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {deletingField ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      <span>Delete Field Only</span>
                    </>
                  )}
                </button>

                {deleteModal.usageCount > 0 && (
                  <button
                    onClick={() => handleDeleteConfirm(true)}
                    disabled={deletingField}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {deletingField ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        <span>Delete Field & Clean Up Data</span>
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={() => setDeleteModal(null)}
                  disabled={deletingField}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldManager;

